import { FilterFieldNames } from '@tableau/vizportal-webclient-api/lib/api/types/IFilterClause';
import { IHttpService } from 'angular';

import { appStore } from '../../app/AppStore';
import { SessionUserSelectors } from '../../app/selectors/SessionInfoSelectors';
import { ServiceLocator } from '../../app/ServiceLocator';
import { FavoritesResource } from '../../favorites/resources/FavoritesResource';
import { IListFetcher } from '../fetchers/IListFetcher';
import { ListResult } from '../fetchers/ListResult';
import { ContentQueryBuilder } from '../helpers/ContentQueryBuilder';
import { Filter } from '../helpers/Filter';
import { MetricMapper } from '../mappers/MetricMapper';
import { IMetricInfo } from '../models/IMetricInfo';
import { ILeafFilterClause } from '../ServerApi/types/IFilterClause';
import { BaseParamsBuilderResourceQuery, getPage } from './ResourceQuery';

// Change endpoint to absolute path if you want to run a mixed local scenario
// e.g. local - 'http://localhost:8080/content'
//      kube -  'content-exploration/content'
// $MARK-jhallmann-20190423: need a story for dev/production configuration
export const CRASS_URI: string = 'content-exploration/content';

export class MetricsResourceQuery<T> extends BaseParamsBuilderResourceQuery<T> {

  constructor(private readonly fetcher: IListFetcher<T>) {
    super();
  }

  public getSlice(start: number, size: number): ng.IPromise<ListResult<T>> {
    const filtersArrayCopy = this.getCopyOfFiltersAndEnsureMetricTypeContent();

    // check filters for favorite
    // if yes, query favorite service and replace filter with id's that are favorited
    const favoriteFilterIndex = filtersArrayCopy.findIndex((f: any) => {
      return f.field === FilterFieldNames.isFavorite;
    });
    if (favoriteFilterIndex > -1) {
      filtersArrayCopy.splice(favoriteFilterIndex, 1);
      const favoritesResource = new FavoritesResource();
      const userId = SessionUserSelectors.sessionUserId(appStore.getState());
      return favoritesResource.getFavorites(userId).then(favorites => {
        if(!favorites.length){
          const $q: ng.IQService = ServiceLocator.get<ng.IQService>('$q');
          let deferred = $q.defer<ListResult<T>>();
          deferred.resolve(ListResult.empty<T>());
          return deferred.promise;
        }
        const listOfFavoriteIDs = favorites.map(f => f.contentId);
        const contentIdFilter = Filter.stringFieldInArray('contentId', listOfFavoriteIDs);
        filtersArrayCopy.push(contentIdFilter);
        return this.queryContent(filtersArrayCopy, start, size, false);
      });
    } else {
      return this.queryContent(filtersArrayCopy, start, size, true);
    }
  }

  private getCopyOfFiltersAndEnsureMetricTypeContent(): ILeafFilterClause[] {
    const filtersArrayCopy = [...this.getFilters()];

    let filter: any = filtersArrayCopy.find((f: any) => f.field === 'contentType');
    if (filter) {
      filter.value = 'metric';
    } else {
      filter = Filter.stringFieldEq('contentType', 'metric');
      filtersArrayCopy.push(filter);
    }
    return filtersArrayCopy;
  }

  private queryContent(filters: ILeafFilterClause[], start: number, size: number, matchFavorites: Boolean){
    const http = ServiceLocator.get<IHttpService>('$http');

    const url = new ContentQueryBuilder(CRASS_URI)
                                           .withFilterClauses(filters)
                                           .withSortClauses(this.getOrder())
                                           .withPaging(getPage(start, size))
                                           .build();


    const contentRequestConfig: ng.IRequestConfig = {
      method: 'GET',
      url
    };

    return http(contentRequestConfig)
      .then((contentResult: any)=> {
        return this.parseMetricContentFromResult(contentResult);
      })
      .then((content: ListResult<any>) => {
        if(matchFavorites){
          // retrieve favorites and match with content items
          const favoritesResource = new FavoritesResource();
          const userId = SessionUserSelectors.sessionUserId(appStore.getState());
          return favoritesResource.getFavorites(userId).then(favorites => {
            const favoriteMap = favorites.reduce((map: any, f: any) => {
              map[f.contentId] = true;
              return map;
            }, {});
            for(let item of content.items) {
              item.favorite = !!favoriteMap['' + item.id];
            }
            return content;
          });
        } else {
          // We are only fetching favorited items
          for(let item of content.items) {
            item.favorite = true;
          }
          return content;
        }
      });
  }

  private parseMetricContentFromResult(result: any){
    let total = result.data.total;
    let items: Array<any> = result.data.items;
    let content: IMetricInfo[] = [];

    if(items){
      content = MetricMapper.map(items);
    }

    return new ListResult<any>(content, content.length, total);
  }

  public getFirst(): ng.IPromise<T> {
    return this.getSlice(0, 1).then((result: ListResult<T>) => {
        if (result.items && result.items.length > 0) {
          return result.items[0];
        } else {
          return null;
        }
      }
    );
  }

  public getCount(): ng.IPromise<number> {
    return this.getSlice(0, 0).then((slice: ListResult<T>) => slice.total);
  }

  public getFetcher() {
    //TODO: this will always return null. Should we create a fetcher that wraps the http call?
    return this.fetcher;
  }
}
